import { ChangeEvent } from 'react';

import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import cx from 'classnames';
import isURL from 'validator/lib/isURL';

import { useLayout } from 'lib/common/contexts/layout/LayoutContext';

import Button from 'lib/common/components/Button';
import ClickableIcon from 'lib/common/components/ClickableIcon';
import { Input } from 'lib/common/components/Input';
import ReactSelect from 'lib/common/components/ReactSelect';

import { IReference } from '../useCreateAgentTask';

import '../styles/create-agent-task.scss';
import '../styles/task-references.scss';

type TTypeSelectPayload = { value: IReference['type'] };

const TYPE_OPTIONS = [
  { label: 'Text', value: 'STRING' },
  { label: 'URL', value: 'URL' },
];

const REFERENCE_LINK_ERROR_HELP_TEXT = 'Reference links should be a valid URL';

function buildChangeEventFromSelectPayload(newType: IReference['type']) {
  return { target: { value: newType || 'STRING' } } as ChangeEvent<HTMLInputElement>;
}

export default function TaskReferences({
  references,
  onUpdateReference,
  removeReference,
  addReference,
}: {
  references: IReference[];
  onUpdateReference: (index: number, field: 'name' | 'value' | 'type') => (e: ChangeEvent<HTMLInputElement>) => void;
  removeReference: (index: number) => () => void;
  addReference: () => void;
}) {
  const { isSoftphone } = useLayout();

  return (
    <div
      className={cx('create-agent-task', {
        'create-agent-task--padding': isSoftphone,
      })}
    >
      {references.map((reference, index) => {
        const isInvalidReference = Boolean(reference.value && reference.type === 'URL' && !isURL(reference.value));

        const onChangeType = ({ value: newType }: TTypeSelectPayload) =>
          onUpdateReference(index, 'type')(buildChangeEventFromSelectPayload(newType));

        return (
          <div className="task-references__reference" key={reference.id}>
            <div>
              <div className="create-agent-task__row create-agent-task__text-field">
                <Input
                  ariaRequired
                  label="Name"
                  fullWidth
                  value={reference.name}
                  onChange={onUpdateReference(index, 'name')}
                  data-testid="create-task-reference-name-input"
                />
                <ReactSelect
                  value={TYPE_OPTIONS.find((option) => option.value === reference.type)}
                  label="Type"
                  options={TYPE_OPTIONS}
                  onChange={onChangeType}
                  isSearchable={false}
                  aria-label="Change reference type"
                  className="task-references__type-select"
                />
              </div>
              <div className="create-agent-task__text-field">
                <Input
                  id="references-value"
                  ariaRequired
                  label="Value"
                  fullWidth
                  value={reference.value}
                  error={isInvalidReference}
                  helperText={isInvalidReference && REFERENCE_LINK_ERROR_HELP_TEXT}
                  type={reference.type === 'URL' ? 'url' : 'text'}
                  onChange={onUpdateReference(index, 'value')}
                  data-testid="create-task-reference-value-input"
                />
              </div>
            </div>
            <ClickableIcon aria-label="Remove reference" icon={faTrash} onClick={removeReference(index)} />
          </div>
        );
      })}
      {isSoftphone && (
        <Button
          className={cx('task-references__button', { 'task-references__button--only-child': !references.length })}
          onClick={addReference}
          round={false}
          styleType="SECONDARY"
          size="small"
        >
          Add Reference
        </Button>
      )}
    </div>
  );
}
